import { useId, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { InvoiceSeriesEntity } from "containers/invoice-series/invoice-series-container.slice";
import { EditPanel, StatusBanner } from "containers/settings/ui";
import {
  errorTextCls,
  helpTextCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import {
  createInvoiceSeries,
  updateInvoiceSeries,
} from "services/series.service";
import {
  CUSTOM,
  DAILY,
  MONTHLY,
  NO_DATE,
  PRESETS,
  YEARLY,
  renderPreview,
  validateTemplate,
} from "features/invoice-series/number-format";

interface InvoiceSeriesFormProps {
  /** The series being edited; none when creating a new one. */
  series?: InvoiceSeriesEntity;
  onSaved: () => void;
  onCancel: () => void;
}

/**
 * Create or edit one sales invoice numbering register, in a panel on the
 * invoice series page.
 *
 * `label` is the token printed on the invoice; `title` is a name for humans.
 * They are separate fields because only the label reaches an invoice — so a
 * rename is always safe, and only the label freezes once the series has issued.
 *
 * Once issued, `label`, the format and the starting number are read-only. The
 * way to a new format is a NEW series, selected in Orders settings — which is
 * also how a company starts fresh on 1 January without renumbering anything.
 */
const InvoiceSeriesForm: React.FC<InvoiceSeriesFormProps> = ({
  series,
  onSaved,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const fieldId = useId();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  const [title, setTitle] = useState(series?.title ?? "");
  const [label, setLabel] = useState(series?.label ?? "");
  const [description, setDescription] = useState(series?.description ?? "");
  const savedTemplate = series?.numberTemplate || NO_DATE;
  const [template, setTemplate] = useState(savedTemplate);
  const [mode, setMode] = useState<string>(
    PRESETS.includes(savedTemplate) ? savedTemplate : CUSTOM
  );
  const [numberStart, setNumberStart] = useState(
    series?.numberStart != null ? String(series.numberStart) : ""
  );

  const locked = !!series?.numberingLocked;

  const presetOptions = useMemo(
    () => [
      {
        label: t("invoice_number_preset_none", {
          defaultValue: "No date — one continuous sequence",
        }),
        value: NO_DATE,
      },
      {
        label: t("invoice_number_preset_year", { defaultValue: "Year" }),
        value: YEARLY,
      },
      {
        label: t("invoice_number_preset_month", { defaultValue: "Month" }),
        value: MONTHLY,
      },
      {
        label: t("invoice_number_preset_day", { defaultValue: "Day" }),
        value: DAILY,
      },
      {
        label: t("invoice_number_preset_custom", { defaultValue: "Custom" }),
        value: CUSTOM,
      },
    ],
    [t]
  );

  const templateError = mode === CUSTOM ? validateTemplate(template) : null;

  // The label is sanitized to what will actually print, so "sf 2026" previews
  // as SF2026 rather than looking like it will keep its spaces.
  const effectiveLabel = useMemo(
    () => renderPreview("{SERIES}", label || title, 1),
    [label, title]
  );

  // Once a series has issued, its true next number depends on `lastSeq` /
  // `currentPeriod` (a dated format may have rolled over to a new year, month
  // or day since the last issue) — exactly what the list endpoint's
  // `numberPreview` is computed from (invoice-series/get-fetch-list.js).
  // Recomputing "numberStart, numberStart+1" here, as the create/unlocked
  // form below still does, showed the series' FIRST number as if it were
  // next — reusing the server's own figure instead of re-deriving it keeps
  // this from drifting out of sync with that logic again.
  const preview = useMemo(() => {
    if (locked) return series?.numberPreview || "";
    const start = Number(numberStart) || 1;
    try {
      return [
        renderPreview(template, label || title, start),
        renderPreview(template, label || title, start + 1),
      ].join(", ");
    } catch (e) {
      return "";
    }
  }, [locked, series, template, numberStart, label, title]);

  const handlePreset = (value: string) => {
    setMode(value);
    if (value !== CUSTOM) setTemplate(value);
  };

  const handleSubmit = async () => {
    if (!companyId) return;
    if (templateError) {
      setError(t(templateError.key, templateError.opts));
      return;
    }
    if (!title.trim()) {
      setError(
        t("invoice_series_name_required", {
          defaultValue: "A name is required",
        })
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (series) {
        // Only what this form owns. Numbering fields are omitted once locked so
        // an unchanged value cannot trip the freeze check.
        const payload: Record<string, any> = { title, description };
        if (!locked) {
          payload.label = label || title;
          payload.numberTemplate = template;
          payload.numberStart = numberStart === "" ? null : Number(numberStart);
        }
        await updateInvoiceSeries(companyId, series.id, payload);
      } else {
        await createInvoiceSeries(companyId, {
          title,
          label: label || title,
          description,
          numberTemplate: template,
          numberStart: numberStart === "" ? null : Number(numberStart),
        });
      }
      toast.success(
        t("invoice_series_saved", { defaultValue: "Invoice series saved" })
      );
      onSaved();
    } catch (err: any) {
      // The backend message names the real problem — a label already taken, an
      // unusable template, a series that has already issued. These codes get
      // their own (LT-able) text because their English sentence bakes in the
      // clashing label's raw name (invoice-number-format.js
      // `assertNoLabelPrefixClash`/`assertNoFinvaldaSeriesClash`/post-create.js's
      // duplicate-label check), which cannot be re-translated after the fact.
      const code = err?.response?.data?.code;
      setError(
        code === "INVOICE_SERIES_LABEL_TAKEN"
          ? t("invoice_series_label_taken", {
              defaultValue:
                "Another series of this company already prints as this label. Choose a different one.",
            })
          : code === "INVOICE_SERIES_LABEL_PREFIX"
          ? t("invoice_series_label_prefix", {
              defaultValue:
                "This label and another series' label begin the same way, so their invoice numbers could coincide. Choose a label that neither begins with another series' label nor is the beginning of one.",
            })
          : code === "INVOICE_SERIES_LABEL_FINVALDA"
          ? t("invoice_series_label_finvalda", {
              defaultValue:
                "This label collides with a series used by invoices imported from Finvalda — choose another label.",
            })
          : err?.response?.data?.message ||
            t("invoice_series_save_failed", {
              defaultValue: "Could not save the invoice series.",
            })
      );
    } finally {
      setSaving(false);
    }
  };

  const ids = {
    name: `${fieldId}-name`,
    label: `${fieldId}-label`,
    description: `${fieldId}-description`,
    mode: `${fieldId}-mode`,
    format: `${fieldId}-format`,
    start: `${fieldId}-start`,
  };

  return (
    <EditPanel
      saving={saving}
      error={error}
      onSave={handleSubmit}
      onCancel={onCancel}
    >
      <div className="space-y-4">
        {locked ? (
          <StatusBanner
            tone="info"
            title={t("invoice_series_locked", {
              count: series?.issuedCount || 0,
              defaultValue:
                "This series has issued {{count}} invoice(s), so its label, format and starting number can no longer change. Create a new series to start a different format — the invoices already issued keep their numbers.",
            })}
          />
        ) : null}

        <div>
          <label className={labelCls} htmlFor={ids.name}>
            {t("invoice_series_name", { defaultValue: "Name" })}
          </label>
          <input
            id={ids.name}
            className={inputCls}
            autoComplete="off"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <p className={helpTextCls}>
            {t("invoice_series_name_hint", {
              defaultValue:
                "For people. Never appears on an invoice, so it can always be changed.",
            })}
          </p>
        </div>

        <div>
          <label className={labelCls} htmlFor={ids.label}>
            {t("invoice_series_label", { defaultValue: "Label" })}
          </label>
          <input
            id={ids.label}
            className={inputCls}
            autoComplete="off"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={locked}
          />
          <p className={helpTextCls}>
            {t("invoice_series_label_hint", {
              example: effectiveLabel,
              defaultValue:
                "The code printed on the invoice. Letters and digits only — this will print as {{example}}.",
            })}
          </p>
        </div>

        <div>
          <label className={labelCls} htmlFor={ids.description}>
            {t("description", { defaultValue: "Description" })}
          </label>
          <input
            id={ids.description}
            className={inputCls}
            autoComplete="off"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor={ids.mode}>
            {t("invoice_number_reset", {
              defaultValue: "Restart the counter every",
            })}
          </label>
          <select
            id={ids.mode}
            className={inputCls}
            value={mode}
            onChange={(e) => handlePreset(e.target.value)}
            disabled={locked}
          >
            {presetOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className={helpTextCls}>
            {t("invoice_number_reset_hint", {
              defaultValue:
                "No date is the simplest to defend: the numbers run in issue order, so a draft dated last month can still be confirmed today. A dated format refuses that.",
            })}
          </p>
        </div>

        {mode === CUSTOM ? (
          <div>
            <label className={labelCls} htmlFor={ids.format}>
              {t("invoice_number_template", { defaultValue: "Format" })}
            </label>
            <input
              id={ids.format}
              className={`${inputCls} font-mono`}
              autoComplete="off"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              disabled={locked}
              aria-invalid={!!templateError}
            />
            {templateError ? (
              <p className={errorTextCls}>
                {t(templateError.key, templateError.opts)}
              </p>
            ) : (
              <p className={helpTextCls}>
                {t("invoice_number_template_hint", {
                  defaultValue:
                    "Tokens: {SERIES}, {YYYY} or {YY}, {MM}, {DD}, and {N} or {N:4} to pad the counter.",
                })}
              </p>
            )}
          </div>
        ) : null}

        <div>
          <label className={labelCls} htmlFor={ids.start}>
            {t("invoice_number_start", { defaultValue: "Start at" })}
          </label>
          <input
            id={ids.start}
            className={inputCls}
            type="number"
            min={1}
            step={1}
            autoComplete="off"
            placeholder="1"
            value={numberStart}
            onChange={(e) => setNumberStart(e.target.value)}
            disabled={locked}
          />
          <p className={helpTextCls}>
            {t("invoice_number_start_hint", {
              defaultValue:
                "Where the counter starts — set 1051 to continue a series from another system. A format without a date uses it once, for the first invoice; a dated format starts every new year, month or day from it.",
            })}
          </p>
        </div>

        {preview ? (
          <p className="rounded-lg bg-white px-3 py-2 text-sm text-zinc-600">
            {locked
              ? t("invoice_number_preview_one", {
                  example: preview,
                  defaultValue: "Next invoice will read: {{example}}",
                })
              : t("invoice_number_preview", {
                  example: preview,
                  defaultValue: "Next invoices will read: {{example}}",
                })}
          </p>
        ) : null}
      </div>
    </EditPanel>
  );
};

export default InvoiceSeriesForm;
